import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock } from "lucide-react";
import { useConsent } from "../lib/consent/store";
import type { ConsentCategories } from "../lib/consent/types";

interface Row {
  key: keyof ConsentCategories;
  label: string;
  description: string;
  locked?: boolean;
}

const ROWS: Row[] = [
  {
    key: "strictly_necessary",
    label: "Strictly necessary",
    description:
      "Required for the site to work: session, security, and your consent choices. Always on.",
    locked: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Helps us understand what is working (GA4, PostHog). Aggregated, anonymised usage data.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Lets us reach the right people through ads and measure campaign performance (Meta Pixel).",
  },
  {
    key: "functional",
    label: "Functional",
    description:
      "Remembers optional preferences (language, region) to give you a smoother experience.",
  },
];

export default function ConsentPreferences() {
  const { state, accept, isPreferencesOpen, closePreferences } = useConsent();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState<ConsentCategories>({
    strictly_necessary: true,
    analytics: state?.categories.analytics ?? false,
    marketing: state?.categories.marketing ?? false,
    functional: state?.categories.functional ?? false,
  });

  useEffect(() => {
    if (isPreferencesOpen) {
      setDraft({
        strictly_necessary: true,
        analytics: state?.categories.analytics ?? false,
        marketing: state?.categories.marketing ?? false,
        functional: state?.categories.functional ?? false,
      });
    }
  }, [isPreferencesOpen, state]);

  useEffect(() => {
    if (!isPreferencesOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePreferences();
    };
    document.addEventListener("keydown", onKey);
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusable = dialogRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, [isPreferencesOpen, closePreferences]);

  const toggle = (key: keyof ConsentCategories) => {
    if (key === "strictly_necessary") return;
    setDraft((d) => ({ ...d, [key]: !d[key] }));
  };

  const save = () => {
    accept({
      analytics: draft.analytics,
      marketing: draft.marketing,
      functional: draft.functional,
    });
    closePreferences();
  };

  return (
    <AnimatePresence>
      {isPreferencesOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closePreferences}
          aria-hidden={!isPreferencesOpen}
        >
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-prefs-title"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-paper border border-ink/15 w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden relative"
          >
            <div className="flex items-start justify-between p-6 border-b-2 border-ink/10">
              <div>
                <h2
                  id="consent-prefs-title"
                  className="text-xl font-serif text-ink"
                >
                  Cookie preferences
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose which cookies LetsLoop can use. You can change this any
                  time.
                </p>
              </div>
              <button
                type="button"
                onClick={closePreferences}
                aria-label="Close cookie preferences"
                className="text-ink/40 hover:text-ink transition-colors p-1 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {ROWS.map((row) => {
                const checked = draft[row.key];
                return (
                  <div
                    key={row.key}
                    className="flex items-start justify-between gap-4 py-3 border-b border-ink/10 last:border-b-0"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-ink">{row.label}</h3>
                        {row.locked && (
                          <Lock
                            size={12}
                            className="text-ink/40"
                            aria-label="Always on"
                          />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {row.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={checked}
                      aria-label={`Toggle ${row.label}`}
                      disabled={row.locked}
                      onClick={() => toggle(row.key)}
                      className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon focus-visible:ring-offset-2 ${
                        checked ? "bg-salmon" : "bg-ink/20"
                      } ${row.locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          checked ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t-2 border-ink/10 bg-paper-tan">
              <button
                type="button"
                onClick={closePreferences}
                className="px-4 py-2.5 rounded-xl text-sm font-semibold text-ink hover:text-salmon transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                className="px-5 py-2.5 rounded-xl text-sm font-bold bg-salmon text-on-salmon hover:bg-salmon transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon focus-visible:ring-offset-2"
              >
                Save preferences
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function CookiePreferencesLink({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  const { openPreferences } = useConsent();
  return (
    <button
      type="button"
      onClick={openPreferences}
      className={
        className ??
        "text-ink/50 hover:text-salmon transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-salmon rounded"
      }
    >
      {children ?? "Cookie preferences"}
    </button>
  );
}
